import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import styles from './style'
import { withStyles, TextField, Divider, Typography, Tooltip } from '@material-ui/core'
import { QuestionFieldNames } from '../../model/card'
import { cardPatchAction, getCardAction } from 'src/reducers/card/action'
import { PatchContent, PatchData } from 'src/model/patch'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useHistory } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { CoreIconButton } from '../core'
import SectionPopover from 'src/components/popover'
import translator from 'src/translate'

import { CardSettingsTypes, TranslateCardTypes } from 'src/model/card'

import { guid } from 'src/utils/guid'
import ModalContent from 'src/components/card-item/card-settings'
import {
  messageBoxShowAction,
  messageBoxHideAction,
} from 'src/reducers/message-box/action'
import MessageBoxContent, {
  MessageBoxType,
  MessageBoxVariant,
} from 'src/model/message-box'
import { ButtonData, ButtonType, ButtonActionType } from 'src/model/button'
import { courseRemoveCard, cardCloneAction } from 'src/reducers/card/action'
import findNextCardAfterDeletion from 'src/hooks/findNextCardAfterDeletion'
import { useCourseSettings } from 'src/hooks/useCourseSettings'
import { useParams } from 'react-router-dom'
import TextToSpeech from '@minicoursegenerator/text-to-speech'
import { courseGetAction } from 'src/reducers/course/action'
import { useSnackbar } from 'notistack'
import { track } from '@amplitude/analytics-browser'

const COPY_FLAG_KEY = 'mcg-show-paste-button'
const COPY_SOURCE_TYPE_KEY = 'mcg-copy-source-type'

const resolvePasteContentType = () => {
  const sourceType = localStorage.getItem(COPY_SOURCE_TYPE_KEY)
  if (['block', 'page', 'landing_page'].includes(sourceType)) {
    return sourceType
  }
  return 'unknown'
}

const CardTitle = props => {
  const { classes, sections, setSections } = props
  const dispatch = useDispatch()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const cardId = searchParams.get('page')
  const course = useSelector(state => state.course.course)
  const entity = useSelector(state => state.card.currentCard)
  const history = useHistory()
  const { subPage } = useParams()
  const { enqueueSnackbar } = useSnackbar()

  const [openPopup, setOpenPopup] = React.useState(null)
  const [clipboardHasEditorContent, setClipboardHasEditorContent] = useState(false)
  const [showPasteButton, setShowPasteButton] = useState(
    !!localStorage.getItem(COPY_FLAG_KEY),
  )
  const [title, setTitle] = useState('')
  const refreshTimeoutRef = useRef(null)

  // Memoize course settings to prevent unnecessary re-renders
  const themeSettings = useCourseSettings('theme')
  const generalSettings = useCourseSettings('general')

  const font = useMemo(() => {
    return themeSettings.value ? themeSettings.value.font : null
  }, [themeSettings.value])

  const valueSettings = useMemo(() => {
    return generalSettings.value || {}
  }, [generalSettings.value])

  // Memoize the initial settings function to prevent recreation on every render
  const getInitialPageReaderSettings = useCallback(() => {
    if (
      valueSettings.pageReaderSettings &&
      valueSettings.pageReaderSettings.textToSpeech
    ) {
      const tts = valueSettings.pageReaderSettings.textToSpeech
      return {
        textToSpeech: {
          value: typeof tts.value === 'boolean' ? tts.value : true,
          mode: tts.mode || (tts.value === 'advanced' ? 'advanced' : 'basic'),
          language: tts.language,
          voice: tts.voice || '',
        },
      }
    }
    return {
      textToSpeech: {
        value: false,
        mode: 'basic',
        voice: '',
      },
    }
  }, [valueSettings])

  // Memoize initial settings to prevent unnecessary recalculation
  const initialPageReaderSettings = useMemo(() => {
    return getInitialPageReaderSettings()
  }, [getInitialPageReaderSettings])

  const [pageReaderSettings, setPageReaderSettings] = useState(
    initialPageReaderSettings,
  )

  // Only update pageReaderSettings when the memoized initial settings actually change
  useEffect(() => {
    const newSettings = getInitialPageReaderSettings()
    // Deep comparison to prevent unnecessary updates
    if (JSON.stringify(newSettings) !== JSON.stringify(pageReaderSettings)) {
      setPageReaderSettings(newSettings)
    }
  }, [getInitialPageReaderSettings]) // Remove pageReaderSettings from dependency

  // Re-check localStorage + clipboard on window focus and after any copy action
  // If user copies something else, paste button disappears and localStorage is cleared
  useEffect(() => {
    const checkPasteButton = () => {
      const hasFlag = !!localStorage.getItem(COPY_FLAG_KEY)
      if (!hasFlag) {
        localStorage.removeItem(COPY_SOURCE_TYPE_KEY)
        setShowPasteButton(false)
        return
      }
      navigator.clipboard.readText().then(text => {
        if (text && text.includes('class="simple-box')) {
          setShowPasteButton(true)
        } else {
          localStorage.removeItem(COPY_FLAG_KEY)
          localStorage.removeItem(COPY_SOURCE_TYPE_KEY)
          setShowPasteButton(false)
        }
      }).catch(() => {
        localStorage.removeItem(COPY_FLAG_KEY)
        localStorage.removeItem(COPY_SOURCE_TYPE_KEY)
        setShowPasteButton(false)
      })
    }
    // Check after any copy event (Ctrl+C etc.) with small delay for clipboard to update
    const onCopy = () => setTimeout(checkPasteButton, 100)
    window.addEventListener('focus', checkPasteButton)
    document.addEventListener('copy', onCopy)
    // Listen for custom event from CKEditor block copy button
    window.addEventListener('mcg-editor-copy', checkPasteButton)
    return () => {
      window.removeEventListener('focus', checkPasteButton)
      document.removeEventListener('copy', onCopy)
      window.removeEventListener('mcg-editor-copy', checkPasteButton)
    }
  }, [])

  // Only update title when entity ID or title actually changes
  useEffect(() => {
    if (entity && entity.title !== title) {
      setTitle(entity.title || '')
    }
  }, [entity?.id, entity?.title]) // Remove title from dependency

  const handleClosePopup = useCallback(() => {
    setOpenPopup(null)
  }, [])

  const settingsClickHandler = useCallback(() => {
    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          buttons: [],
          title: TranslateCardTypes[entity[QuestionFieldNames.QUESTION_TYPE]]
            ? 'Translate'
            : translator._('action.settings'),
          component: (
            <ModalContent
              cardId={entity.id}
              {...props}
              key={guid.uid}
              closeThisModal={() => {
                dispatch(messageBoxHideAction())
              }}
            />
          ),
          maxWidth: '50vw',
        }),
      ),
    )
  }, [dispatch, entity, props])

  const handleDeletePopup = useCallback(
    event => {
      dispatch(
        messageBoxShowAction(
          MessageBoxContent({
            type: MessageBoxType.ERROR,
            buttons: [
              new ButtonData(
                ButtonType.NORMAL,
                'Cancel',
                ButtonActionType.NO,
                e => {
                  dispatch(messageBoxHideAction())
                },
              ),
              new ButtonData(
                ButtonType.ACTION,
                'Delete',
                ButtonActionType.DELETE,
                e => {
                  const nextCard = findNextCardAfterDeletion(
                    Number(subPage),
                    sections,
                  )
                  let newSections = sections
                  newSections = newSections.map(section => {
                    section.cards = section.cards.filter(
                      sectionCard => sectionCard.id !== Number(subPage),
                    )
                    if (section.cards.length === 0) {
                      return null
                    } else return section
                  })

                  newSections = newSections.filter(
                    section => section !== null && section.cards.length > 0,
                  )

                  dispatch(
                    courseRemoveCard(course.id, Number(subPage), () => {
                      setSections(newSections)
                    }),
                  )
                  if (nextCard) {
                    history.push(
                      `/mini-course/${course.id}/page/${nextCard.id}`,
                    )
                    dispatch(getCardAction(nextCard.id))
                  } else {
                    dispatch(getCardAction(null))
                    history.push(`/mini-course/${course.id}/page/`)
                  }

                  dispatch(messageBoxHideAction())
                },
              ),
            ],
            title: 'Delete Page',
            note: '',
            description: 'You are about to delete this page.',
            variant: MessageBoxVariant.NOTICE,
          }),
        ),
      )
    },
    [dispatch, course, subPage, sections, setSections, history],
  )

  const handleCopy = useCallback(async () => {
    if (window.editor) {
      const data = window.editor.getData()
      await navigator.clipboard.writeText(data)
      localStorage.setItem(COPY_FLAG_KEY, '1')
      localStorage.setItem(COPY_SOURCE_TYPE_KEY, 'page')
      setShowPasteButton(true)
      track('page_copied', {
        copy_content: 'page',
        page_context: 'page_editor',
      })
      enqueueSnackbar('Page copied to clipboard', { variant: 'success' })
    }
  }, [enqueueSnackbar])

  const handlePaste = useCallback(() => {
    if (!window.editor) return
    navigator.clipboard.readText().then(data => {
      if (!data || !data.includes('class="simple-box')) return
      const viewFragment = window.editor.data.processor.toView(data)
      const modelFragment = window.editor.data.toModel(viewFragment)
      window.editor.model.change(writer => {
        const root = window.editor.model.document.getRoot()
        writer.insert(modelFragment, root, 0)
      })
      track('pasted_to_page', {
        paste_content: resolvePasteContentType(),
        page_context: 'page_editor',
      })
      localStorage.removeItem(COPY_FLAG_KEY)
      localStorage.removeItem(COPY_SOURCE_TYPE_KEY)
      setShowPasteButton(false)
      enqueueSnackbar('Copied content pasted to top of this page', { variant: 'success' })
    })
  }, [enqueueSnackbar])

  const handleDismiss = () => {
    localStorage.removeItem(COPY_FLAG_KEY)
    localStorage.removeItem(COPY_SOURCE_TYPE_KEY)
    setShowPasteButton(false)
  }

  const handleDuplicate = useCallback(() => {
    dispatch(
      cardCloneAction(Number(subPage), course.id, newCard => {
        dispatch(courseGetAction(course.id))
        if (newCard && newCard.id) {
          history.push(`/mini-course/${course.id}/page/${newCard.id}`)
          dispatch(getCardAction(newCard.id))
          track('page_duplicated', { page_context: 'page_editor' })
          enqueueSnackbar('Page duplicated', { variant: 'success' })
        }
      }),
    )
  }, [dispatch, course, subPage, history, enqueueSnackbar])

  const onBlur = useCallback(
    async value => {
      if (entity[QuestionFieldNames.TITLE] !== value) {
        dispatch(
          cardPatchAction(
            new PatchData(
              new PatchContent(
                value,
                QuestionFieldNames.TITLE,
                PatchContent.OPERATION_REPLACE,
              ),
              entity[QuestionFieldNames.ID],
            ),
            () => {
              let newSections = sections.map(section => {
                if (section.id === entity.sectionId) {
                  return {
                    ...section,
                    cards: section.cards.map(card => {
                      if (card.id === entity.id) {
                        return {
                          ...card,
                          [QuestionFieldNames.TITLE]: value,
                        }
                      }
                      return card
                    }),
                  }
                }
                return section
              })

              setSections(newSections)

              // Clear any existing timeout
              if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current)
              }

              // Update Redux courseCards state to keep left side in sync
              // Use a small delay to prevent excessive API calls
              refreshTimeoutRef.current = setTimeout(() => {
                if (course?.id) {
                  dispatch(courseGetAction(course.id))
                }
              }, 500)
            },
          ),
        )
      }
    },
    [dispatch, entity, sections, setSections, course],
  )

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
      }
    }
  }, [])

  const handleTitleChange = useCallback(e => {
    setTitle(e.target.value)
  }, [])

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div className={classes.root}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            justifyContent: 'center',
          }}
        >
          <TextField
            placeholder="Untitled Page"
            className={`${classes.textfield} ${
              CardSettingsTypes[entity?.type] &&
              CardSettingsTypes[entity?.type].settings
                ? classes.textfieldWithSettings
                : ''
            }`}
            InputProps={{
              className: classes.textfieldInput,
              style: {
                fontFamily: font || 'Inter',
              },
            }}
            multiline
            minRows={1}
            maxRows={2}
            inputProps={{
              style: {
                textAlign: 'center',
                padding: 0,
              },
            }}
            onBlur={e => onBlur(e.target.value)}
            onChange={handleTitleChange}
            value={title || ''}
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            {CardSettingsTypes[entity?.type] &&
              CardSettingsTypes[entity?.type].settings && (
                <>
                  <CoreIconButton
                    size="small"
                    variant="text"
                    color="dark"
                    onClick={settingsClickHandler}
                    id="setting-button"
                  >
                    {TranslateCardTypes[entity?.type] ? (
                      <Icon icon="material-symbols:translate" />
                    ) : (
                      <Icon icon="ion:settings-outline" />
                    )}
                  </CoreIconButton>
                </>
              )}
            <Tooltip title="Copy page" placement="bottom">
              <CoreIconButton
                size="small"
                variant="text"
                color="dark"
                onClick={event => {
                  event.stopPropagation()
                  handleCopy()
                }}
              >
                <Icon icon="si:copy-line" />
              </CoreIconButton>
            </Tooltip>
            <>
              <CoreIconButton
                size="small"
                variant="text"
                color="secondary"
                onClick={event => {
                  event.stopPropagation()
                  setOpenPopup(event.currentTarget)
                  navigator.clipboard.readText().then(text => {
                    setClipboardHasEditorContent(
                      !!text && text.includes('class="simple-box'),
                    )
                  }).catch(() => {
                    setClipboardHasEditorContent(false)
                  })
                }}
              >
                <Icon icon={'eva:more-vertical-fill'} />
              </CoreIconButton>
            </>
          </div>
        </div>
        <Divider className={classes.divider} variant="middle" />
        {(!title || title == '' || title == null) && (
          <Typography
            color="#FB9D01"
            fontFamily={'Inter'}
            fontSize="14px"
            fontWeight={500}
          >
            Please enter the "Page Title"
          </Typography>
        )}
        {showPasteButton && (
          <div className={classes.pasteBarContainer}>
            <div className={classes.pasteBarInfo}>
              <Icon icon="iconoir:paste-clipboard" className={classes.pasteIcon} />
              <Typography className={classes.pasteText}>
                Paste the copied block here
              </Typography>
            </div>
            <button
              type="button"
              className={classes.pasteButton}
              onClick={event => {
                event.stopPropagation()
                handlePaste()
              }}
            >
              Paste
            </button>
            <button
              type="button"
              className={classes.dismissButton}
              aria-label="Dismiss paste banner"
              onClick={event => {
                event.stopPropagation()
                handleDismiss()
              }}
            >
              <Icon icon="eva:close-outline" />
            </button>
          </div>
        )}
        {pageReaderSettings.textToSpeech.value && (
          <div
            className={classes.player}
            style={{ display: 'flex', alignItems: 'center', gap: 12 }}
          >
            <div
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                }}
                onMouseEnter={e => {
                  const tooltip = e.currentTarget.querySelector('.info-tooltip')
                  if (tooltip) tooltip.style.display = 'block'
                }}
                onMouseLeave={e => {
                  const tooltip = e.currentTarget.querySelector('.info-tooltip')
                  if (tooltip) tooltip.style.display = 'none'
                }}
              >
                <Icon
                  icon="eva:info-outline"
                  style={{
                    color: 'grey',
                    zIndex: '9',
                    background: '#fff',
                    borderRadius: '50%',
                    fontSize: 22,
                    padding: 2,
                  }}
                />
                <div
                  className="info-tooltip"
                  style={{
                    display: 'none',
                    position: 'absolute',
                    left: 28,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: '#00364A',
                    color: '#fff',
                    borderRadius: 12,
                    padding: '8px 16px',
                    fontSize: 12,
                    fontWeight: 500,
                    whiteSpace: 'pre-line',
                    zIndex: 10,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                    minWidth: 260,
                  }}
                >
                  Audio playback is available only in view mode.
                </div>
              </div>
            </div>
            <div style={{ width: '100%', maxWidth: '752px', height: '50px' }}>
              <div
                style={{ opacity: 0.5, pointerEvents: 'none', height: '50px' }}
              >
                <TextToSpeech
                  style={{
                    opacity: 0.5,
                    pointerEvents: 'none',
                    height: '50px',
                  }}
                  type="basic"
                  height="50px"
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <SectionPopover
        openPopup={openPopup}
        handleClosePopup={event => {
          if (event) {
            event.stopPropagation()
          }
          handleClosePopup()
        }}
        itemData={[
          {
            text: 'Paste',
            icon: 'iconoir:paste-clipboard',
            action: handlePaste,
            disabled: !clipboardHasEditorContent,
          },
          {
            text: 'Duplicate',
            icon: 'system-uicons:duplicate',
            action: handleDuplicate,
          },
          {
            text: 'Delete Page',
            icon: 'iconoir:trash',
            action: handleDeletePopup,
          },
        ]}
      />
    </div>
  )
}

export default withStyles(styles)(CardTitle)
